import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import Navigation from "../../Navigation";
import Footer from "../../Footer";
import mermaid from "mermaid";

const CryptoAgent = () => {
  const { language } = useLanguage();
  const sectionRefs = useRef([]);
  const diagramRef = useRef(null);

  // 初始化Mermaid
  useEffect(() => {
    // 先配置Mermaid
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "loose", // 允许点击事件
      theme: "neutral",
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: "basis",
      },
    });

    // 如果图表容器存在，重新渲染图表
    if (diagramRef.current) {
      try {
        // 使用最新的run API代替contentLoaded
        mermaid
          .run({
            nodes: [diagramRef.current],
          })
          .catch((error) => {
            console.error("Mermaid渲染失败:", error);
          });
      } catch (error) {
        console.error("Mermaid执行出错:", error);
      }
    }
  }, [language]); // 当语言变化时重新渲染

  // 翻译
  const translations = {
    backToProjects: {
      en: "Back to Projects",
      zh: "返回项目列表",
      ja: "プロジェクトに戻る",
    },
    title: {
      en: "Crypto Trading Agent System",
      zh: "加密货币交易代理系统",
      ja: "暗号通貨取引エージェントシステム",
    },
    tagline: {
      en: "A modular, extensible automated trading framework",
      zh: "一个模块化、可扩展的自动化交易框架",
      ja: "モジュール式で拡張可能な自動取引フレームワーク",
    },
    overview: {
      en: "Project Overview",
      zh: "项目概述",
      ja: "プロジェクト概要",
    },
    description: {
      en: "The cryptocurrency trading agent system is a modular, extensible automated trading framework that integrates various analysis techniques, including technical analysis, on-chain analysis, market sentiment analysis, and risk management. It adopts a multi-agent collaborative architecture, where specialized agents work together to provide comprehensive market insights.",
      zh: "加密货币交易代理系统是一个模块化、可扩展的自动化交易框架，集成了各种分析技术，包括技术分析、链上分析、市场情绪分析和风险管理。系统采用多代理协作架构，各专业代理相互协作，提供全面的市场洞察。",
      ja: "暗号通貨取引エージェントシステムは、テクニカル分析、オンチェーン分析、市場センチメント分析、リスク管理など、さまざまな分析手法を統合したモジュール式で拡張可能な自動取引フレームワークです。専門エージェントが連携して包括的な市場洞察を提供するマルチエージェント協調アーキテクチャを採用しています。",
    },
    workflow: {
      en: "Multi-Agent Workflow",
      zh: "多代理工作流",
      ja: "マルチエージェントワークフロー",
    },
    workflowText: {
      en: "The system adopts a multi-layer agent architecture, processing data from market collection through multi-dimensional analysis, multi-perspective research, and debate integration to final decision-making, forming a complete closed-loop system. Each agent focuses on its specific responsibility, collaborating to provide comprehensive market insights.",
      zh: "此架构设计基于多层级代理协作，数据从市场数据收集、多维度分析、多角度研究、辩论整合到最终决策形成一个完整的闭环系统。各代理专注于自己的职责，通过协作提供全面的市场洞察。",
      ja: "このシステムは多層エージェントアーキテクチャを採用し、市場データ収集から多次元分析、多角的研究、議論統合を経て最終的な意思決定に至るまでのデータを処理し、完全なクローズドループシステムを形成します。各エージェントは特定の責任に焦点を当て、協力して包括的な市場洞察を提供します。",
    },
    agents: {
      en: "Agent Modules",
      zh: "代理模块",
      ja: "エージェントモジュール",
    },
    technical: {
      en: "Technical Analysis",
      zh: "技术分析",
      ja: "テクニカル分析",
    },
    technicalText: {
      en: "Analyzes price and volume patterns to identify potential trading opportunities based on historical market data and established technical indicators.",
      zh: "分析价格和交易量模式，基于历史市场数据和已建立的技术指标识别潜在交易机会。",
      ja: "過去の市場データと確立されたテクニカル指標に基づいて、価格と取引量のパターンを分析し、潜在的な取引機会を特定します。",
    },
    onchain: {
      en: "On-chain Analysis",
      zh: "链上分析",
      ja: "オンチェーン分析",
    },
    onchainText: {
      en: "Examines blockchain data to identify significant transactions, whale activity, network health, and DeFi metrics that could impact asset prices.",
      zh: "检查区块链数据以识别可能影响资产价格的重要交易、鲸鱼活动、网络健康状况和DeFi指标。",
      ja: "資産価格に影響を与える可能性のある重要な取引、ホエール活動、ネットワークの健全性、DeFi指標を特定するためにブロックチェーンデータを調査します。",
    },
    sentiment: {
      en: "Sentiment Analysis",
      zh: "情绪分析",
      ja: "センチメント分析",
    },
    sentimentText: {
      en: "Processes social media and news sources to gauge market sentiment and identify potential shifts in investor psychology that could affect market movements.",
      zh: "处理社交媒体和新闻来源，衡量市场情绪并识别可能影响市场走势的投资者心理变化。",
      ja: "ソーシャルメディアやニュースソースを処理して市場感情を測定し、市場の動きに影響を与える可能性のある投資家心理の潜在的な変化を特定します。",
    },
    research: {
      en: "Research Agents",
      zh: "研究代理",
      ja: "研究エージェント",
    },
    researchText: {
      en: "Specialized agents that analyze market data from bullish and bearish perspectives to provide balanced insights and counter potential biases in analysis.",
      zh: "专门的代理从看多和看空的角度分析市场数据，提供平衡的见解，抵消分析中的潜在偏见。",
      ja: "強気と弱気の視点から市場データを分析し、バランスの取れた洞察を提供し、分析における潜在的な偏りに対抗する専門エージェント。",
    },
    debate: {
      en: "Debate Room",
      zh: "辩论室",
      ja: "討論室",
    },
    debateText: {
      en: "An integration layer where conflicting analyses are evaluated and synthesized to form more robust trading recommendations.",
      zh: "一个整合层，对冲突的分析进行评估和综合，形成更健全的交易建议。",
      ja: "相反する分析を評価し、より堅牢な取引推奨事項を形成するための統合レイヤー。",
    },
    risk: {
      en: "Risk Management",
      zh: "风险管理",
      ja: "リスク管理",
    },
    riskText: {
      en: "Evaluates potential risks associated with trading decisions and adjusts position sizing based on market volatility and portfolio considerations.",
      zh: "评估与交易决策相关的潜在风险，并根据市场波动性和投资组合考虑调整头寸大小。",
      ja: "取引決定に関連する潜在的なリスクを評価し、市場のボラティリティとポートフォリオの考慮事項に基づいてポジションのサイジングを調整します。",
    },
    features: {
      en: "System Features",
      zh: "系统特点",
      ja: "システム特徴",
    },
    customization: {
      en: "Customization",
      zh: "可定制性",
      ja: "カスタマイズ",
    },
    customizationText: {
      en: "Flexible configuration options and extensible architecture that allows users to adapt the system to their trading strategies and preferences.",
      zh: "灵活的配置选项和可扩展架构，允许用户根据自己的交易策略和偏好调整系统。",
      ja: "柔軟な構成オプションと拡張可能なアーキテクチャにより、ユーザーはシステムを自分の取引戦略や好みに適応させることができます。",
    },
    backtesting: {
      en: "Backtesting",
      zh: "回测系统",
      ja: "バックテスト",
    },
    backtestingText: {
      en: "Comprehensive strategy backtesting and performance evaluation to assess the effectiveness of trading algorithms before deploying them in live markets.",
      zh: "全面的策略回测和性能评估，在将交易算法部署到实盘市场之前评估其有效性。",
      ja: "取引アルゴリズムをライブ市場に展開する前に、その有効性を評価するための包括的な戦略バックテストとパフォーマンス評価。",
    },
    multiData: {
      en: "Multi-source Data",
      zh: "多源数据",
      ja: "複数ソースデータ",
    },
    multiDataText: {
      en: "Integration of exchange, on-chain, and social media data to provide a comprehensive view of market conditions and potential trading opportunities.",
      zh: "集成交易所、链上和社交媒体数据，提供全面的市场状况和潜在交易机会视图。",
      ja: "市場状況と潜在的な取引機会の包括的なビューを提供するための取引所、オンチェーン、ソーシャルメディアデータの統合。",
    },
    conclusion: {
      en: "Conclusion",
      zh: "结论",
      ja: "結論",
    },
    conclusionText: {
      en: "The Crypto Trading Agent System demonstrates the potential of multi-agent architectures in creating robust trading systems. By combining diverse analysis techniques and implementing a collaborative decision-making process, the system provides a comprehensive approach to cryptocurrency trading that addresses the complexity and volatility of these markets.",
      zh: "加密货币交易代理系统展示了多代理架构在创建健壮交易系统方面的潜力。通过结合多种分析技术并实施协作决策过程，该系统提供了一种全面的加密货币交易方法，应对这些市场的复杂性和波动性。",
      ja: "暗号通貨取引エージェントシステムは、堅牢な取引システムを作成する上でのマルチエージェントアーキテクチャの可能性を示しています。多様な分析手法を組み合わせ、協調的な意思決定プロセスを実装することで、このシステムは、これらの市場の複雑性と変動性に対処する包括的な暗号通貨取引アプローチを提供します。",
    },
    githubLink: {
      en: "View on GitHub",
      zh: "在GitHub上查看",
      ja: "GitHubで表示",
    },
  };

  // 技术栈数据
  const techStack = [
    { name: "Python", category: "language", color: "blue" },
    { name: "Machine Learning", category: "technology", color: "purple" },
    { name: "Blockchain Analysis", category: "technology", color: "purple" },
    { name: "Technical Analysis", category: "concept", color: "green" },
    { name: "Sentiment Analysis", category: "concept", color: "green" },
    { name: "Risk Management", category: "concept", color: "green" },
    { name: "Multi-agent System", category: "architecture", color: "orange" },
    { name: "Backtesting", category: "feature", color: "amber" },
    { name: "API Integration", category: "feature", color: "amber" },
  ];

  // 按类别获取技术标签的颜色
  const getTagColor = (category) => {
    switch (category) {
      case "language":
        return "bg-blue-100 text-blue-800";
      case "technology":
        return "bg-purple-100 text-purple-800";
      case "concept":
        return "bg-green-100 text-green-800";
      case "architecture":
        return "bg-orange-100 text-orange-800";
      case "feature":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50">
      <Navigation activeSection="projects" />
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-6 flex justify-between items-center">
            <Link
              to="/projects"
              className="text-primary-500 hover:text-primary-600 transition-colors flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {translations.backToProjects[language] ||
                translations.backToProjects.en}
            </Link>

            {/* GitHub链接 */}
            <a
              href="https://github.com/markjailenechan/crypto-trading-agent"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="mr-2"
              >
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              {translations.githubLink[language] || translations.githubLink.en}
            </a>
          </div>

          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-light mb-4 text-neutral-800">
              {translations.title[language] || translations.title.en}
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              {translations.tagline[language] || translations.tagline.en}
            </p>

            {/* 技术栈标签 */}
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {techStack.map((tech, index) => (
                <span
                  key={index}
                  className={`${getTagColor(
                    tech.category
                  )} px-3 py-1 rounded-full text-sm font-medium`}
                >
                  {tech.name}
                </span>
              ))}
            </div>
          </header>

          {/* 项目概述 */}
          <section
            ref={(el) => (sectionRefs.current[0] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.overview[language] || translations.overview.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.description[language] ||
                translations.description.en}
            </p>

            {/* 项目封面图 */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
              <div className="p-4 overflow-auto">
                {/* 使用ref引用图表容器，并使用pre标签而不是div以确保更好的兼容性 */}
                <pre ref={diagramRef} className="mermaid text-xs md:text-sm">
                  {`flowchart TD
    subgraph ${
      language === "zh"
        ? "工作流控制器"
        : language === "ja"
        ? "ワークフローコントローラー"
        : "Workflow Controller"
    }
        Config[${
          language === "zh"
            ? "配置加载"
            : language === "ja"
            ? "構成の読み込み"
            : "Load Config"
        }] --> |${
                    language === "zh"
                      ? "初始化"
                      : language === "ja"
                      ? "初期化"
                      : "Initialize"
                  }| Start
        Start[${
          language === "zh"
            ? "开始分析"
            : language === "ja"
            ? "分析開始"
            : "Start Analysis"
        }] --> MarketData

        %% ${
          language === "zh"
            ? "数据收集阶段"
            : language === "ja"
            ? "データ収集段階"
            : "Data Collection Phase"
        }
        MarketData[${
          language === "zh"
            ? "市场数据代理"
            : language === "ja"
            ? "市場データエージェント"
            : "Market Data Agent"
        }] --> |${
                    language === "zh"
                      ? "收集数据"
                      : language === "ja"
                      ? "データ収集"
                      : "Collect Data"
                  }| Technical
        MarketData --> |${
          language === "zh"
            ? "收集数据"
            : language === "ja"
            ? "データ収集"
            : "Collect Data"
        }| Onchain
        MarketData --> |${
          language === "zh"
            ? "收集数据"
            : language === "ja"
            ? "データ収集"
            : "Collect Data"
        }| Sentiment
        MarketData --> |${
          language === "zh"
            ? "收集数据"
            : language === "ja"
            ? "データ収集"
            : "Collect Data"
        }| Valuation

        %% ${
          language === "zh"
            ? "分析阶段"
            : language === "ja"
            ? "分析段階"
            : "Analysis Phase"
        }
        subgraph ${
          language === "zh"
            ? "分析代理"
            : language === "ja"
            ? "分析エージェント"
            : "Analysis Agents"
        }
            Technical[${
              language === "zh"
                ? "技术分析"
                : language === "ja"
                ? "テクニカル分析"
                : "Technical Analysis"
            }]
            Onchain[${
              language === "zh"
                ? "链上分析"
                : language === "ja"
                ? "オンチェーン分析"
                : "On-chain Analysis"
            }]
            Sentiment[${
              language === "zh"
                ? "情绪分析"
                : language === "ja"
                ? "センチメント分析"
                : "Sentiment Analysis"
            }]
            Valuation[${
              language === "zh"
                ? "估值分析"
                : language === "ja"
                ? "評価分析"
                : "Valuation Analysis"
            }]
        end

        %% ${
          language === "zh"
            ? "研究阶段"
            : language === "ja"
            ? "研究段階"
            : "Research Phase"
        }
        Technical --> BearResearch
        Onchain --> BearResearch
        Sentiment --> BearResearch
        Valuation --> BearResearch

        Technical --> BullResearch
        Onchain --> BullResearch
        Sentiment --> BullResearch
        Valuation --> BullResearch

        subgraph ${
          language === "zh"
            ? "研究代理"
            : language === "ja"
            ? "研究エージェント"
            : "Research Agents"
        }
            BearResearch[${
              language === "zh"
                ? "看空研究代理"
                : language === "ja"
                ? "弱気研究エージェント"
                : "Bearish Research"
            }]
            BullResearch[${
              language === "zh"
                ? "看多研究代理"
                : language === "ja"
                ? "強気研究エージェント"
                : "Bullish Research"
            }]
            DebateRoom[${
              language === "zh"
                ? "辩论室"
                : language === "ja"
                ? "討論室"
                : "Debate Room"
            }]
        end

        BearResearch --> DebateRoom
        BullResearch --> DebateRoom

        %% ${
          language === "zh"
            ? "决策阶段"
            : language === "ja"
            ? "意思決定段階"
            : "Decision Phase"
        }
        DebateRoom --> RiskManagement

        subgraph ${
          language === "zh"
            ? "决策与执行"
            : language === "ja"
            ? "意思決定と実行"
            : "Decision & Execution"
        }
            RiskManagement[${
              language === "zh"
                ? "风险管理"
                : language === "ja"
                ? "リスク管理"
                : "Risk Management"
            }]
            PortfolioManagement[${
              language === "zh"
                ? "投资组合管理"
                : language === "ja"
                ? "ポートフォリオ管理"
                : "Portfolio Management"
            }]
            Execution[${
              language === "zh"
                ? "交易执行"
                : language === "ja"
                ? "取引実行"
                : "Trade Execution"
            }]
        end

        RiskManagement --> PortfolioManagement
        PortfolioManagement --> Decision{${
          language === "zh"
            ? "交易决策?"
            : language === "ja"
            ? "取引決定?"
            : "Trade Decision?"
        }}

        %% ${
          language === "zh"
            ? "执行阶段"
            : language === "ja"
            ? "実行段階"
            : "Execution Phase"
        }
        Decision -->|${
          language === "zh"
            ? "买入/卖出"
            : language === "ja"
            ? "買う/売る"
            : "Buy/Sell"
        }| Execution
        Decision -->|${
          language === "zh" ? "持有" : language === "ja" ? "保持" : "Hold"
        }| End

        Execution --> End[${
          language === "zh"
            ? "结束分析循环"
            : language === "ja"
            ? "分析ループの終了"
            : "End Analysis Loop"
        }]

        %% ${
          language === "zh"
            ? "结果反馈"
            : language === "ja"
            ? "結果フィードバック"
            : "Result Feedback"
        }
        End --> |${
          language === "zh"
            ? "保存结果"
            : language === "ja"
            ? "結果を保存"
            : "Save Results"
        }| Results[(${
                    language === "zh"
                      ? "结果存储"
                      : language === "ja"
                      ? "結果ストレージ"
                      : "Results Storage"
                  })]
        End --> |${
          language === "zh"
            ? "发送通知"
            : language === "ja"
            ? "通知を送信"
            : "Send Notification"
        }| Notification[${
                    language === "zh"
                      ? "通知系统"
                      : language === "ja"
                      ? "通知システム"
                      : "Notification System"
                  }]
    end`}
                </pre>
              </div>
              <div className="mt-4 text-sm text-gray-600 text-center pb-4">
                {language === "zh"
                  ? "多代理系统工作流程图 - 展示不同代理间的数据流和交互模式"
                  : language === "ja"
                  ? "マルチエージェントシステムワークフロー図 - 異なるエージェント間のデータフローと相互作用パターンを示す"
                  : "Multi-agent System Workflow Diagram - showing data flow and interaction patterns between different agents"}
              </div>
            </div>
          </section>

          {/* 多代理工作流 */}
          <section
            ref={(el) => (sectionRefs.current[1] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.workflow[language] || translations.workflow.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.workflowText[language] ||
                translations.workflowText.en}
            </p>
          </section>

          {/* 代理模块 */}
          <section
            ref={(el) => (sectionRefs.current[2] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.agents[language] || translations.agents.en}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* 技术分析 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-3 text-neutral-800">
                    {translations.technical[language] ||
                      translations.technical.en}
                  </h3>
                  <p className="text-neutral-600">
                    {translations.technicalText[language] ||
                      translations.technicalText.en}
                  </p>
                </div>
              </div>

              {/* 链上分析 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-3 text-neutral-800">
                    {translations.onchain[language] || translations.onchain.en}
                  </h3>
                  <p className="text-neutral-600">
                    {translations.onchainText[language] ||
                      translations.onchainText.en}
                  </p>
                </div>
              </div>

              {/* 情绪分析 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-3 text-neutral-800">
                    {translations.sentiment[language] ||
                      translations.sentiment.en}
                  </h3>
                  <p className="text-neutral-600">
                    {translations.sentimentText[language] ||
                      translations.sentimentText.en}
                  </p>
                </div>
              </div>

              {/* 风险管理 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-3 text-neutral-800">
                    {translations.risk[language] || translations.risk.en}
                  </h3>
                  <p className="text-neutral-600">
                    {translations.riskText[language] ||
                      translations.riskText.en}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 系统特点 */}
          <section
            ref={(el) => (sectionRefs.current[3] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.features[language] || translations.features.en}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* 可定制性 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-3 text-center text-neutral-800">
                    {translations.customization[language] ||
                      translations.customization.en}
                  </h3>
                  <p className="text-neutral-600 text-center">
                    {translations.customizationText[language] ||
                      translations.customizationText.en}
                  </p>
                </div>
              </div>

              {/* 回测系统 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-3 text-center text-neutral-800">
                    {translations.backtesting[language] ||
                      translations.backtesting.en}
                  </h3>
                  <p className="text-neutral-600 text-center">
                    {translations.backtestingText[language] ||
                      translations.backtestingText.en}
                  </p>
                </div>
              </div>

              {/* 多源数据 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-3 text-center text-neutral-800">
                    {translations.multiData[language] ||
                      translations.multiData.en}
                  </h3>
                  <p className="text-neutral-600 text-center">
                    {translations.multiDataText[language] ||
                      translations.multiDataText.en}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 结论 */}
          <section
            ref={(el) => (sectionRefs.current[4] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.conclusion[language] || translations.conclusion.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.conclusionText[language] ||
                translations.conclusionText.en}
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CryptoAgent;
